import React, { useState, useCallback } from 'react';
import api from '../../../helpers/axios';

const C = {
	bg:      '#0a0a1a',
	surface: '#0e0e1c',
	border:  'rgba(255,255,255,0.08)',
	primary: '#7c6deb',
	danger:  '#e74c3c',
	dangerBg:'rgba(231,76,60,0.08)',
	dangerBorder: 'rgba(231,76,60,0.25)',
	teal:    '#46bcaa',
	amber:   '#f39c12',
	textPri: '#ffffff',
	textSec: 'rgba(255,255,255,0.55)',
	textMeta:'rgba(255,255,255,0.32)',
} as const;

const inputStyle: React.CSSProperties = {
	background: 'rgba(255,255,255,0.04)',
	border: `1px solid ${C.border}`,
	borderRadius: '10px',
	padding: '12px 16px',
	color: C.textPri,
	fontSize: '14px',
	outline: 'none',
	width: '100%',
	transition: 'border-color 0.2s',
	fontFamily: 'inherit',
};

type Step = 'info' | 'otp' | 'done';

const ReporterDeleteAccountPage: React.FC = () => {
	const [step, setStep]       = useState<Step>('info');
	const [email, setEmail]     = useState('');
	const [otp, setOtp]         = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError]     = useState('');

	const handleSendOtp = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email.trim()) return;
		setSending(true);
		setError('');
		try {
			await api.post('/api/public/reporter/deletion/request', { email: email.trim() });
			setStep('otp');
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.response?.data?.errorMessage || 'Something went wrong. Please try again.');
		} finally {
			setSending(false);
		}
	}, [email]);

	const handleConfirm = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		if (!otp.trim()) return;
		setSending(true);
		setError('');
		try {
			await api.post('/api/public/reporter/deletion/confirm', { email: email.trim(), otp: otp.trim() });
			setStep('done');
		} catch (err: any) {
			setError(err?.response?.data?.errorMessage || err?.response?.data?.message || 'Something went wrong. Please try again.');
		} finally {
			setSending(false);
		}
	}, [email, otp]);

	return (
		<div style={{
			position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
			overflowY: 'auto', background: C.bg,
			fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
			color: C.textPri, zIndex: 9000,
		}}>

			{/* Nav */}
			<nav style={{
				position: 'sticky', top: 0, zIndex: 100,
				background: 'rgba(10,10,26,0.9)', backdropFilter: 'blur(20px)',
				borderBottom: '1px solid rgba(255,255,255,0.06)',
				padding: '0 24px',
			}}>
				<div style={{ maxWidth: 860, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<a href='/'>
						<img src='/logobanner.png' alt='Just Know' style={{ height: 32, objectFit: 'contain' }} />
					</a>
					<a href='/' style={{ color: C.textSec, fontSize: 14, textDecoration: 'none' }}>← Back to Home</a>
				</div>
			</nav>

			{/* Page content */}
			<div style={{ maxWidth: 860, margin: '0 auto', padding: '56px 24px 80px' }}>

				{/* Header */}
				<div style={{ marginBottom: 48 }}>
					<div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, borderRadius: '50px', padding: '5px 14px', marginBottom: '20px' }}>
						<span style={{ color: C.danger, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Account Deletion</span>
					</div>
					<h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 900, letterSpacing: '-1.5px', marginBottom: 14 }}>Delete Reporter Account</h1>
					<p style={{ color: C.textSec, fontSize: 15, lineHeight: 1.75, maxWidth: 600 }}>
						You can permanently delete your JustKnow Reporter account at any time.
						Once confirmed, your account will be immediately deactivated and you will be signed out of all devices.
					</p>
					<p style={{ color: C.textMeta, fontSize: 13, marginTop: 10 }}>
						Compliant with DPDPA 2023 · IT Act 2000 · IT Rules 2021 · PMLA 2002
					</p>
				</div>

				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 48, alignItems: 'start' }} className='reporter-del-grid'>

					{/* Left — info panel */}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>What Gets Deleted</div>
							<ul style={{ color: C.textSec, fontSize: 13, lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
								<li>Name, email address, and phone number</li>
								<li>Profile picture and display name</li>
								<li>Notification preferences and FCM tokens</li>
								<li>Location preferences (state, district, pincode)</li>
							</ul>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>What We Retain</div>
							<ul style={{ color: C.textSec, fontSize: 13, lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
								<li>Published articles (shown as "Former Reporter")</li>
								<li>Wallet &amp; payout records — 7 years (Income Tax Act)</li>
								<li>KYC documents — 5 years (PMLA 2002)</li>
								<li>TDS ledger — 7 years (GST regulations)</li>
							</ul>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Prefer In-App?</div>
							<p style={{ color: C.textSec, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
								Open the <strong style={{ color: C.textPri }}>Just Know Reporter</strong> app →
								Profile tab → Delete Account — and follow the on-screen steps.
							</p>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Need Help?</div>
							<a href='mailto:privacy@justknow.co.in' style={{ color: C.primary, fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>
								privacy@justknow.co.in
							</a>
						</div>

					</div>

					{/* Right — action panel */}
					<div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`, borderRadius: 18, padding: 'clamp(24px, 4vw, 36px)', display: 'flex', flexDirection: 'column', gap: 20 }}>

						{step === 'info' && (
							<>
								{/* Warning banner */}
								<div style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, borderRadius: 12, padding: '16px 20px' }}>
									<p style={{ color: C.danger, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>⚠ This action is irreversible</p>
									<p style={{ color: 'rgba(231,76,60,0.8)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
										Deleting your account will immediately deactivate it and sign you out of all devices.
										This cannot be undone.
									</p>
								</div>

								<form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
										<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>Registered Email Address *</label>
										<input
											type='email'
											value={email}
											onChange={e => setEmail(e.target.value)}
											required
											placeholder='you@example.com'
											style={inputStyle}
											onFocus={e => { e.target.style.borderColor = 'rgba(231,76,60,0.55)'; }}
											onBlur={e => { e.target.style.borderColor = C.border; }}
										/>
										<span style={{ color: C.textMeta, fontSize: 12 }}>We'll send a verification code to this email address.</span>
									</div>

									{error && (
										<div style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.22)', borderRadius: 9, padding: '13px 16px', color: '#ff6b6b', fontSize: 13 }}>
											{error}
										</div>
									)}

									<button type='submit' disabled={sending || !email.trim()}
										style={{ background: sending ? 'rgba(231,76,60,0.45)' : C.danger, border: 'none', borderRadius: 10, padding: '13px 28px', color: '#fff', fontSize: 14, fontWeight: 700, cursor: sending || !email.trim() ? 'not-allowed' : 'pointer', transition: 'all 0.25s', opacity: !email.trim() ? 0.5 : 1 }}
										onMouseEnter={e => { if (!sending && email.trim()) (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
										onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}>
										{sending ? 'Sending Code…' : 'Send Verification Code'}
									</button>
								</form>
							</>
						)}

						{step === 'otp' && (
							<>
								<div style={{ background: 'rgba(70,188,170,0.08)', border: '1px solid rgba(70,188,170,0.25)', borderRadius: 12, padding: '14px 18px' }}>
									<p style={{ color: C.teal, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Verification code sent</p>
									<p style={{ color: 'rgba(70,188,170,0.8)', fontSize: 13, margin: 0 }}>
										A 6-digit code has been sent to <strong style={{ color: C.teal }}>{email}</strong>. Valid for 10 minutes.
									</p>
								</div>

								<form onSubmit={handleConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
										<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>6-Digit Verification Code *</label>
										<input
											type='text'
											inputMode='numeric'
											maxLength={6}
											value={otp}
											onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
											required
											placeholder='000000'
											style={{ ...inputStyle, fontSize: '22px', letterSpacing: '8px', textAlign: 'center' }}
											onFocus={e => { e.target.style.borderColor = 'rgba(231,76,60,0.55)'; }}
											onBlur={e => { e.target.style.borderColor = C.border; }}
										/>
									</div>

									{error && (
										<div style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.22)', borderRadius: 9, padding: '13px 16px', color: '#ff6b6b', fontSize: 13 }}>
											{error}
										</div>
									)}

									<button type='submit' disabled={sending || otp.length !== 6}
										style={{ background: sending ? 'rgba(231,76,60,0.45)' : C.danger, border: 'none', borderRadius: 10, padding: '13px 28px', color: '#fff', fontSize: 14, fontWeight: 700, cursor: sending || otp.length !== 6 ? 'not-allowed' : 'pointer', transition: 'all 0.25s', opacity: otp.length !== 6 ? 0.5 : 1 }}>
										{sending ? 'Deleting Account…' : 'Confirm Deletion'}
									</button>

									<button type='button' onClick={() => { setStep('info'); setOtp(''); setError(''); }}
										style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 28px', color: C.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
										← Back
									</button>
								</form>
							</>
						)}

						{step === 'done' && (
							<div style={{ textAlign: 'center', padding: '20px 0' }}>
								<div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
								<h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, color: C.teal }}>Account Deleted</h3>
								<p style={{ color: C.textSec, fontSize: 14, lineHeight: 1.7 }}>
									Your Just Know Reporter account has been permanently deleted.
									Your published articles will continue to appear as authored by "Former Reporter".
								</p>
								<p style={{ color: C.textMeta, fontSize: 13, marginTop: 12 }}>
									Questions? Email <a href='mailto:privacy@justknow.co.in' style={{ color: C.primary }}>privacy@justknow.co.in</a>
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div style={{ borderTop: `1px solid ${C.border}`, marginTop: 64, paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
					<span style={{ color: C.textMeta, fontSize: 13 }}>© {new Date().getFullYear()} Just Know. All rights reserved.</span>
					<div style={{ display: 'flex', gap: 20 }}>
						<a href='/reporter-privacy-policy' style={{ color: C.textMeta, fontSize: 13, textDecoration: 'none' }}>Reporter Privacy Policy</a>
						<a href='/contact-us' style={{ color: C.textMeta, fontSize: 13, textDecoration: 'none' }}>Contact Us</a>
					</div>
				</div>
			</div>

			<style>{`
				@media (max-width: 640px) {
					.reporter-del-grid { grid-template-columns: 1fr !important; }
				}
			`}</style>
		</div>
	);
};

export default ReporterDeleteAccountPage;
